class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: routes.cart_url,
    });

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body },
    })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);

        if (parsedState.errors) {
          console.log('CART UPDATE ERROR:', parsedState);
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        const cartDrawerWrapper = document.querySelector('cart-drawer');

        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
        }

        if (parsedState.sections && parsedState.sections['cart-drawer']) {
          const parsedHTML = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
          const newCartDrawer = parsedHTML.querySelector('#CartDrawer');
          const currentCartDrawer = document.querySelector('#CartDrawer');

          if (newCartDrawer && currentCartDrawer) {
            currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
          }
        }

        if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
          const parsedBubble = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
          const newBubble = parsedBubble.querySelector('.shopify-section');
          const currentBubble = document.getElementById('cart-icon-bubble');

          if (newBubble && currentBubble) {
            currentBubble.innerHTML = newBubble.innerHTML;
          }
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';

        if (typeof updatedValue !== 'undefined' && quantityElement && updatedValue !== parseInt(quantityElement.value)) {
          message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }

        this.updateLiveRegions(line, message);

        if (cartDrawerWrapper) {
          const overlay = cartDrawerWrapper.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', cartDrawerWrapper.close.bind(cartDrawerWrapper));

          const lineItem =
            document.getElementById(`CartDrawer-Item-${line}`) || document.querySelector('.cart-item');

          if (lineItem) {
            const focusTarget = lineItem.querySelector(`[name="${name}"]`) || lineItem.querySelector('.cart-item__name');
            if (focusTarget) trapFocus(cartDrawerWrapper, focusTarget);
          }
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch((error) => {
        console.log('CART DRAWER UPDATE FAILED:', error);
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));

        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
/* =========================================================
   TOLNEX — FIX REAL RECÁLCULO CANTIDAD CART DRAWER
   Intercepta + / - / cambio de cantidad y re-renderiza drawer
   ========================================================= */

(() => {
  if (window.__tolnexCartDrawerQuantityFixLoaded) return;
  window.__tolnexCartDrawerQuantityFixLoaded = true;

  async function tolnexRefreshCartDrawer() {
    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
    const htmlText = await response.text();
    const html = new DOMParser().parseFromString(htmlText, 'text/html');

    const newCartDrawer = html.querySelector('#CartDrawer');
    const currentCartDrawer = document.querySelector('#CartDrawer');

    if (newCartDrawer && currentCartDrawer) {
      currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
    }

    const bubbleResponse = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble`);
    const bubbleText = await bubbleResponse.text();
    const bubbleHTML = new DOMParser().parseFromString(bubbleText, 'text/html');

    const newBubble = bubbleHTML.querySelector('.shopify-section');
    const currentBubble = document.querySelector('#cart-icon-bubble');

    if (newBubble && currentBubble) {
      currentBubble.innerHTML = newBubble.innerHTML;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    const overlay = document.querySelector('#CartDrawer-Overlay');

    if (cartDrawer && overlay) {
      overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
    }
  }

  async function tolnexChangeDrawerQuantity(input, newQuantity) {
    if (!input) return;

    const line = input.dataset.index;
    if (!line) return;

    const safeQuantity = Math.max(0, parseInt(newQuantity, 10) || 0);

    const drawerItems = document.querySelector('cart-drawer-items');
    const cartItemsContainer = document.querySelector('#CartDrawer-CartItems');

    if (cartItemsContainer) {
      cartItemsContainer.classList.add('cart__items--disabled');
    }

    try {
      const response = await fetch(`${routes.cart_change_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          line: Number(line),
          quantity: safeQuantity,
        }),
      });

      const cartState = await response.json();

      if (!response.ok || cartState.errors) {
        console.log('TOLNEX CART CHANGE ERROR:', cartState);
        return;
      }

      await tolnexRefreshCartDrawer();

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.classList.toggle('is-empty', cartState.item_count === 0);
      }

      if (drawerItems) {
        drawerItems.classList.toggle('is-empty', cartState.item_count === 0);
      }
    } catch (error) {
      console.log('TOLNEX CART CHANGE FAILED:', error);
    } finally {
      const updatedCartItemsContainer = document.querySelector('#CartDrawer-CartItems');
      if (updatedCartItemsContainer) {
        updatedCartItemsContainer.classList.remove('cart__items--disabled');
      }
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      const button = event.target.closest('cart-drawer .quantity__button');

      if (!button) return;

      const quantityInput = button.closest('quantity-input');
      const input = quantityInput ? quantityInput.querySelector('.quantity__input') : null;

      if (!input) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const currentValue = parseInt(input.value, 10) || 0;
      const step = parseInt(input.getAttribute('step'), 10) || 1;

      let newQuantity = currentValue;

      if (button.name === 'plus') {
        newQuantity = currentValue + step;
      }

      if (button.name === 'minus') {
        newQuantity = currentValue - step;
      }

      input.value = Math.max(0, newQuantity);

      tolnexChangeDrawerQuantity(input, input.value);
    },
    true
  );

  document.addEventListener(
    'change',
    function (event) {
      const input = event.target.closest('cart-drawer .quantity__input');

      if (!input) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      tolnexChangeDrawerQuantity(input, input.value);
    },
    true
  );
})();